import { useState, useEffect } from 'react';
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { doc, setDoc } from "firebase/firestore";
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

export const isNotificationSupported = () => {
    if (Capacitor.isNativePlatform()) {
        return true;
    }
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

export const useFCMToken = (messaging, user, db, publicDataPath, showNotification) => {
    const [token, setToken] = useState(null);
    const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');

    useEffect(() => {
        const registerPush = async () => {
            console.log('[FCM_DEBUG] Initializing Push Registration...');
            if (Capacitor.isNativePlatform()) {
                console.log('[FCM_DEBUG] Platform is Native Android/iOS');

                // 1. Check existing permission
                let permStatus = await PushNotifications.checkPermissions();
                console.log('[FCM_DEBUG] Initial Permission Status:', JSON.stringify(permStatus));

                // 2. If prompt, request it automatically (for debugging)
                if (permStatus.receive === 'prompt') {
                    console.log('[FCM_DEBUG] Requesting permission...');
                    permStatus = await PushNotifications.requestPermissions();
                    console.log('[FCM_DEBUG] Post-Request Status:', JSON.stringify(permStatus));
                }

                // 3. Register if granted
                if (permStatus.receive === 'granted') {
                    console.log('[FCM_DEBUG] Permission granted. Registering with FCM...');
                    await PushNotifications.register();
                } else {
                    console.log('[FCM_DEBUG] Permission NOT granted. Skipping registration.');
                }

                // Listeners for Native Token
                PushNotifications.addListener('registration', (token) => {
                    console.log('[FCM_DEBUG] Push registration success, token: ' + token.value);
                    setToken(token.value);
                });

                PushNotifications.addListener('registrationError', (error) => {
                    console.error('[FCM_DEBUG] Error on registration: ' + JSON.stringify(error));
                });
            } else {
                console.log('[FCM_DEBUG] Platform is Web');
                // Web Logic
                if (permission === 'granted' && messaging) {
                    getToken(messaging).then(setToken).catch((err) => {
                        console.log('[FCM_DEBUG] An error occurred while retrieving token. ', err);
                    });
                }
            }
        };

        registerPush();

        // Cleanup listeners
        return () => {
            if (Capacitor.isNativePlatform()) {
                PushNotifications.removeAllListeners();
            }
        };

    }, [permission, messaging]);

    // [BARU] Save Token to Firestore
    useEffect(() => {
        const saveToken = async () => {
            console.log(`[FCM_DEBUG] Attempting to save token. Token: ${!!token}, User: ${!!user}, DB: ${!!db}`);
            if (token && user && db) {
                try {
                    // Simpan token di sub-collection fcmTokens user
                    // Path: {publicDataPath}/users/{userId}/fcmTokens/{token}
                    const tokenRef = doc(db, `${publicDataPath}/users/${user.uid}/fcmTokens`, token);
                    await setDoc(tokenRef, {
                        token: token,
                        platform: Capacitor.getPlatform(),
                        updatedAt: new Date(),
                        deviceInfo: {
                            platform: Capacitor.getPlatform(),
                            userAgent: navigator.userAgent
                        }
                    }, { merge: true });
                    console.log("[FCM_DEBUG] FCM Token saved to Firestore for user:", user.email);
                } catch (error) {
                    console.error("[FCM_DEBUG] Error saving FCM token to Firestore:", error);
                }
            }
        };
        saveToken();
    }, [token, user, db, publicDataPath]);

    const requestPermission = async () => {
        if (Capacitor.isNativePlatform()) {
            try {
                const result = await PushNotifications.requestPermissions();
                if (result.receive === 'granted') {
                    setPermission('granted');
                    // Trigger registration immediately after permission grant
                    await PushNotifications.register();
                    return 'granted';
                }
                return 'denied';
            } catch (e) {
                console.error("Native permission error", e);
                return 'denied';
            }
        } else {
            // Web Logic
            try {
                const permission = await Notification.requestPermission();
                setPermission(permission);
                return permission;
            } catch (error) {
                console.error("Error requesting permission", error);
                return 'denied';
            }
        }
    }

    const deleteToken = async () => {
        console.warn("Delete token not implemented.");
    };

    const checkNativePermission = async () => {
        if (Capacitor.isNativePlatform()) {
            const status = await PushNotifications.checkPermissions();
            return status.receive;
        }
        return Notification.permission;
    };

    return {
        fcmToken: token,
        permission,
        requestPermission,
        deleteToken,
        hasPermission: permission === 'granted',
        checkNativePermission // [BARU] Expose native check
    };
};

export const useForegroundMessage = (messaging, onMessageReceived) => {
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            // Native Foreground Listener
            const listener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
                console.log('Push received: ', notification);
                // Convert to similar format as Web payload if needed, or pass raw
                // Web: { notification: { title, body } }
                // Native: { title, body, data }
                if (onMessageReceived) {
                    onMessageReceived({
                        notification: {
                            title: notification.title,
                            body: notification.body
                        },
                        data: notification.data
                    });
                }
            });

            const actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                console.log('Push action performed: ', notification);
                // Handle notification tap logic here if needed or expose another hook
            });

            return () => {
                listener.then(handle => handle.remove());
                actionListener.then(handle => handle.remove());
            };
        } else {
            // Web Foreground Listener
            if (!messaging) return;
            const unsubscribe = onMessage(messaging, (payload) => {
                if (onMessageReceived) onMessageReceived(payload);
            });
            return unsubscribe;
        }
    }, [messaging, onMessageReceived]);
};
